import { BaseRestRepository } from '~/repositories/baseRestRepository';
import { envConfig } from '~/utils/env';
import type {
  ChatAILeaseSettings,
  ChatAIMaintenanceRequestsSettings,
  ChatSettings,
  CommunicationsSettings,
  EmailSettings,
  TextSettings,
} from '~/swagger/settings/Api';

const SETTINGS_BASE_URL = `${envConfig.service.settings}/company-portal/settings`;

const getSettingsUrl = (slug: string) => `${SETTINGS_BASE_URL}/${slug}`;

const getCompanySettingsUrl = (slug: string) => `${getSettingsUrl(slug)}/company`;

const getAllSettingsUrl = (slug: string) => `${getSettingsUrl(slug)}/with-overrides`;

const getLevelSettingsUrl = (slug: string, level: string, levelId: string) => `${getSettingsUrl(slug)}/${level}/${levelId}`;

const getCompanySettingsPayload = <TSettings>(settings: TSettings) => ({ settings });

const getLevelSettingsPayload = <TSettings>(level: string, levelId: string, settings: TSettings) => ({
  settings,
  targets: [{ level, levelId }],
});

export class chatsSettingsRepository extends BaseRestRepository {
  public async changeTextSettings(payload: TextSettings) {
    const url = getCompanySettingsUrl('text');
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(getCompanySettingsPayload(payload)),
    });
    return this.getJsonResponse(response);
  }

  public async changeChatSettings(payload: ChatSettings) {
    const url = getCompanySettingsUrl('chat');
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(getCompanySettingsPayload(payload)),
    });
    return this.getJsonResponse(response);
  }

  public async changeChatAILeaseSettings(payload: ChatAILeaseSettings) {
    const url = getCompanySettingsUrl('chatailease');
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(getCompanySettingsPayload(payload)),
    });
    return this.getJsonResponse(response);
  }

  public async changeChatAIMaintenanceRequestsSettings(payload: ChatAIMaintenanceRequestsSettings) {
    const url = getCompanySettingsUrl('chataimaintenancerequests');
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(getCompanySettingsPayload(payload)),
    });
    return this.getJsonResponse(response);
  }

  public async getAllTextSettings() {
    const url = getAllSettingsUrl('text');
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getChatsSettings() {
    const url = getCompanySettingsUrl('chat');
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getAllChatAILeaseSettings() {
    const url = getAllSettingsUrl('chatailease');
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getAllChatAIMaintenanceRequestsSettings() {
    const url = getAllSettingsUrl('chataimaintenancerequests');
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async updateTextSettingsByLevel(level: string, levelId: string, payload: TextSettings) {
    const url = getSettingsUrl('text');
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(getLevelSettingsPayload(level, levelId, payload)),
    });
    return this.getJsonResponse(response);
  }

  public async updateChatsSettingsByLevel(level: string, levelId: string, payload: ChatSettings) {
    const url = getSettingsUrl('chat');
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(getLevelSettingsPayload(level, levelId, payload)),
    });
    return this.getJsonResponse(response);
  }

  public async deleteTextSettingsByLevel(level: string, levelId: string) {
    const url = getLevelSettingsUrl('text', level, levelId);
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }

  public async deleteChatsSettingsByLevel(level: string, levelId: string) {
    const url = getLevelSettingsUrl('chat', level, levelId);
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }

  public async updateChatAILeaseSettingsByLevel(level: string, levelId: string, payload: ChatAILeaseSettings) {
    const url = getSettingsUrl('chatailease');
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(getLevelSettingsPayload(level, levelId, payload)),
    });
    return this.getJsonResponse(response);
  }

  public async deleteChatAILeaseSettingsByLevel(level: string, levelId: string) {
    const url = getLevelSettingsUrl('chatailease', level, levelId);
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }

  public async updateChatAIMaintenanceRequestsSettingsByLevel(level: string, levelId: string, payload: ChatAIMaintenanceRequestsSettings) {
    const url = getSettingsUrl('chataimaintenancerequests');
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(getLevelSettingsPayload(level, levelId, payload)),
    });
    return this.getJsonResponse(response);
  }

  public async deleteChatAIMaintenanceRequestsSettingsByLevel(level: string, levelId: string) {
    const url = getLevelSettingsUrl('chataimaintenancerequests', level, levelId);
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }

  public async changeEmailSettings(payload: EmailSettings) {
    const url = getCompanySettingsUrl('email');
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(getCompanySettingsPayload(payload)),
    });
    return this.getJsonResponse(response);
  }

  public async getEmailSettings() {
    const url = getCompanySettingsUrl('email');
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async changeCommunicationsSettings(payload: CommunicationsSettings) {
    const url = getCompanySettingsUrl('communications');
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(getCompanySettingsPayload(payload)),
    });
    return this.getJsonResponse(response);
  }

  public async getCommunicationsSettings() {
    const url = getCompanySettingsUrl('communications');
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }
}
